import { useApolloClient } from "@apollo/client";
import { Checkbox } from "@mui/material";
import { Dropdown } from "pepsico-ds";
import { useCallback, useEffect, useMemo, useState } from "react";
import Table from "../../components/common/table/Table";
import JsonFormsWrapper from "../../components/jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import { loadKeywordGroupById } from "../../graphql/queries/loadKeywordGroupById";
import {
  MESSAGES,
  PRODUCT_HEADER_DATA,
  STYLES,
  TABLE_CONFIG,
} from "./productConstants";
import { productFilterSchema, productFilterUISchema } from "./productFormSchema";
import "./productManagement.scss";
import { transformProductsToRowData } from "./productUtils";

const ProductsTable = ({
  products,
  loading,
  filterData,
  productGroups = [],
  productGroupMap = {},
  handleView,
  handleEdit,
  handleSoftDelete,
  handleFilterChange,
  selectable = false,
  selectedProductIds = [],
  onSelectionChange,
}) => {
  const client = useApolloClient();
  const [keywordGroupMap, setKeywordGroupMap] = useState({});
  const [selectedProductGroup, setSelectedProductGroup] = useState([]);

  // Sync dropdown selection when filterData.productGroupId changes externally
  useEffect(() => {
    const groupId = filterData?.productGroupId;
    if (groupId) {
      const match = productGroups.find((g) => g.id === groupId);
      if (match) {
        setSelectedProductGroup([match]);
      }
    } else {
      setSelectedProductGroup([]);
    }
  }, [filterData?.productGroupId, productGroups]);

  // Collect all unique keywordGroupIds from products, excluding product group IDs
  const allKeywordGroupIds = useMemo(() => {
    const productGroupIds = new Set(Object.keys(productGroupMap));
    const ids = new Set();
    products.forEach((p) => {
      (p.keywordsGroupIds || []).forEach((id) => {
        if (!productGroupIds.has(id)) {
          ids.add(id);
        }
      });
    });
    return [...ids];
  }, [products, productGroupMap]);

  // Fetch keyword group names individually by ID
  useEffect(() => {
    if (allKeywordGroupIds.length === 0) {
      setKeywordGroupMap({});
      return;
    }

    const missingIds = allKeywordGroupIds.filter((id) => !keywordGroupMap[id]);
    if (missingIds.length === 0) return;

    let cancelled = false;

    const fetchNames = async () => {
      try {
        const results = await Promise.all(
          missingIds.map((groupId) =>
            client.query({
              query: loadKeywordGroupById,
              variables: { id: groupId },
              fetchPolicy: "cache-first",
            })
          )
        );

        if (cancelled) return;

        const newEntries = {};
        results.forEach((result) => {
          const kg = result?.data?.loadKeyWordGroupById;
          if (kg) {
            newEntries[kg.id] = kg.name;
          }
        });

        setKeywordGroupMap((prev) => ({ ...prev, ...newEntries }));
      } catch (err) {
        console.error("Failed to load keyword group names:", err);
      }
    };

    fetchNames();

    return () => {
      cancelled = true;
    };
  }, [allKeywordGroupIds, client]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle product group dropdown change
  const handleProductGroupChange = useCallback(
    (selected) => {
      setSelectedProductGroup(selected);
      const groupId = selected?.[0]?.id || "";
      handleFilterChange({ ...filterData, productGroupId: groupId });
    },
    [handleFilterChange, filterData]
  );

  // Handle text filter changes (name, sku)
  const handleTextFilterChange = useCallback(
    (data) => {
      handleFilterChange({
        ...data,
        productGroupId: filterData.productGroupId || "",
      });
    },
    [handleFilterChange, filterData]
  );

  const rowData = transformProductsToRowData(
    products,
    keywordGroupMap,
    productGroupMap,
    handleView,
    handleEdit,
    handleSoftDelete
  );

  const allIds = useMemo(() => products.map((p) => p.id), [products]);
  const allSelected =
    selectable &&
    allIds.length > 0 &&
    allIds.every((id) => selectedProductIds.includes(id));
  const someSelected = selectable && selectedProductIds.length > 0 && !allSelected;

  const handleToggleAll = useCallback(() => {
    if (!onSelectionChange) return;
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange([...allIds]);
    }
  }, [allSelected, allIds, onSelectionChange]);

  const handleToggleRow = useCallback(
    (productId) => {
      if (!onSelectionChange) return;
      if (selectedProductIds.includes(productId)) {
        onSelectionChange(selectedProductIds.filter((id) => id !== productId));
      } else {
        onSelectionChange([...selectedProductIds, productId]);
      }
    },
    [selectedProductIds, onSelectionChange]
  );

  const headerData = useMemo(() => {
    if (!selectable) return PRODUCT_HEADER_DATA;

    const checkboxHeader = {
      columnKey: "checkbox",
      header: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={handleToggleAll}
          size="small"
          sx={{ padding: 0 }}
        />
      ),
      className: "product-checkbox-col",
    };
    return [checkboxHeader, ...PRODUCT_HEADER_DATA];
  }, [selectable, allSelected, someSelected, handleToggleAll]);

  const enhancedRowData = useMemo(() => {
    if (!selectable) return rowData;

    return rowData.map((row, index) => {
      const product = products[index];
      return {
        checkbox: (
          <Checkbox
            checked={selectedProductIds.includes(product?.id)}
            onChange={() => handleToggleRow(product?.id)}
            size="small"
            sx={{ padding: 0 }}
          />
        ),
        ...row,
      };
    });
  }, [selectable, rowData, products, selectedProductIds, handleToggleRow]);

  return (
    <div className="products-table-container">
      <div className="products-table-header">
        <div className="filter-wrapper">
          <JsonFormsWrapper
            jsonschema={productFilterSchema}
            uischema={productFilterUISchema}
            data={filterData}
            onChange={handleTextFilterChange}
          />
          <div className="product-group-filter">
            <Dropdown
              childList={productGroups}
              selection="single"
              selectedValue={selectedProductGroup}
              setSelectedValue={handleProductGroupChange}
              placeholder="Product Group"
              size="medium"
              isClearable
            />
          </div>
        </div>
      </div>
      {loading ? (
        <div style={STYLES.loadingContainer}>{MESSAGES.LOADING}</div>
      ) : (
        <Table
          headerData={headerData}
          rowData={enhancedRowData}
          className="products-table"
          pagination={true}
          rowsPerPage={TABLE_CONFIG.ROWS_PER_PAGE}
          noDataLabel={TABLE_CONFIG.NO_DATA_LABEL}
        />
      )}
    </div>
  );
};

export default ProductsTable;
